import React, { useMemo } from 'react';
import facebookMiniLogo from '../../../../assets/facebookMiniLogo.svg';
import googleAdsMiniLogo from '../../../../assets/googleAdsMiniLogo.svg';
import linkedInLogo from '../../../../assets/linked_in_logo.webp';
import statusBAD from '../../../../assets/statusBAD.svg';
import statusOK from '../../../../assets/statusOK.svg';
import tikTokLogo from '../../../../assets/tikTokLogo.webp';
import stylesTable from '../../../../components/reusable/Tables/Tables.module.css';
import { parceBudget, parcePercentage } from '../../../../helpers/parceBudget';
import { formatNumber } from '../../../../utils/formatNumber';
import Table from '../../Table';
import { formater } from '../DashboardTableRows/functions';
import styles from './styles.module.css';
import ComparisonChangeValue from '../ComparisonChangeValue';

const CampaignPerformanceTable = ({
  data,
  type,
  metrics,
  isLoading,
  isAllChecked,
  isShowComparison,

  handleClickClient,
  handleClickBudget,
  handleCheckAll,
  handleCheckOne,
}) => {
  const getColumns = useMemo(() => {
    // Define columns based on metrics
    const columns = [
      {
        title: (
          <input
            className={stylesTable.checkbox}
            type="checkbox"
            checked={isAllChecked}
            onChange={handleCheckAll}
          />
        ),
        classNameHeaderCell: stylesTable.thInput,
        className: stylesTable.td,
        width: '50px',
        minWidth: '50px',
        fixed: 'left',
        enableColumnResizing: false,
        render: (record, _) => {
          return (
            record.row_type !== 'total' && (
              <input
                className={stylesTable.checkbox}
                type="checkbox"
                checked={record.isChecked}
                onChange={() => {
                  if (handleCheckOne)
                    handleCheckOne(formater('key', record, type));
                }}
              />
            )
          );
        },
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        fixed: 'left',
        sorter: true,
        className: `${stylesTable.td}`,
        style: ({ row_type }, _) => ({
          ...(row_type === 'total'
            ? { cursor: 'not-allowed' }
            : { cursor: 'pointer' }),
        }),
        render: ({ source, row_type }, _) => {
          let renderContent = <></>;

          if (row_type === 'total') renderContent = 'Total';
          if (source === 'G')
            renderContent = (
              <div className={stylesTable.flexCenter}>
                <img src={googleAdsMiniLogo} alt="google_logo" />
              </div>
            );
          if (source === 'F')
            renderContent = (
              <div className={stylesTable.flexCenter}>
                <img src={facebookMiniLogo} alt="facebook_logo" />
              </div>
            );

          if (source === 'T')
            renderContent = (
              <div className={stylesTable.flexCenter}>
                <img
                  src={tikTokLogo}
                  alt="tiktok_logo"
                  className={styles.sourceImg}
                />
              </div>
            );

          if (source === 'L')
            renderContent = (
              <div className={stylesTable.flexCenter}>
                <img
                  src={linkedInLogo}
                  alt="linked_in_logo"
                  className={styles.sourceImg}
                />
              </div>
            );

          return renderContent;
        },
      },
      {
        title: 'Campaign',
        dataIndex: 'campaign_name',
        key: 'campaign_name',
        fixed: 'left',
        sorter: true,
        className: `${stylesTable.tdNoWidth}`,
        render: ({ status, campaign_name }, _) => {
          return (
            <div className={styles.campaignNameCell}>
              <div className={styles.campaignNameCellIcon}>
                {status === 'E' && <img src={statusOK} />}
                {status === 'P' && <img src={statusBAD} />}
              </div>
              <span className={stylesTable.tdImageSpan}>{campaign_name}</span>
            </div>
          );
        },
      },
      {
        title: 'Client',
        dataIndex: 'client_name',
        key: 'client_name',
        fixed: 'left',
        sorter: true,
        className: `${stylesTable.td}`,
        style: { color: '#1649EC', cursor: 'pointer' },
        onClickDataCell: (render, _) => {
          if (handleClickClient) handleClickClient(render);
        },
      },
      {
        title: 'Budget',
        dataIndex: 'budget_name',
        key: 'budget_name',
        fixed: 'left',
        sorter: true,
        className: `${stylesTable.td}`,
        style: { color: '#1649EC', cursor: 'pointer' },
        onClickDataCell: (render, _) => {
          if (handleClickBudget) handleClickBudget(render);
        },
      },
      {
        title: 'Impressions',
        dataIndex: 'impressions',
        key: 'impressions',
        sorter: true,
        className: `${stylesTable.td}`,
        render: (
          { impressions, row_type, previous_period_impressions_change },
          _,
        ) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{formatNumber(impressions)}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue
                  noColor
                  value={previous_period_impressions_change}
                />
              )}
            </div>
          );
        },
      },
      {
        title: 'Clicks',
        dataIndex: 'clicks',
        key: 'clicks',
        sorter: true,
        className: `${stylesTable.td}`,
        render: ({ clicks, row_type, previous_period_clicks_change }, _) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{formatNumber(clicks)}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue
                  noColor
                  value={previous_period_clicks_change}
                />
              )}
            </div>
          );
        },
      },
      {
        title: 'Spend',
        dataIndex: 'spend',
        key: 'spend',
        sorter: true,
        className: `${stylesTable.td}`,
        render: (
          { spend, currency, row_type, previous_period_spend_change },
          _,
        ) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{parceBudget(spend, currency)}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue noColor value={previous_period_spend_change} />
              )}
            </div>
          );
        },
      },
      {
        title: 'Conversions',
        dataIndex: 'conversions',
        key: 'conversions',
        sorter: true,
        className: `${stylesTable.td}`,
        render: (
          { conversions, previous_period_conversions_change, row_type },
          _,
        ) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{formatNumber((+conversions)?.toFixed(0))}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue
                  value={previous_period_conversions_change}
                />
              )}
            </div>
          );
        },
      },
      {
        title: 'CPA',
        dataIndex: 'cpa',
        key: 'cpa',
        sorter: true,
        className: `${stylesTable.td}`,
        render: (
          { cpa, currency, previous_period_cpa_change, row_type },
          _,
        ) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{parceBudget(cpa, currency)}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue value={previous_period_cpa_change} />
              )}
            </div>
          );
        },
      },
      {
        title: 'Revenue',
        dataIndex: 'revenue',
        key: 'revenue',
        sorter: true,
        className: `${stylesTable.td}`,
        render: (
          { revenue, currency, previous_period_revenue_change, row_type },
          _,
        ) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{parceBudget(revenue, currency)}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue value={previous_period_revenue_change} />
              )}
            </div>
          );
        },
      },
      {
        title: 'ROAS',
        dataIndex: 'roas',
        key: 'roas',
        sorter: true,
        className: `${stylesTable.td}`,
        render: ({ roas, previous_period_roas_change, row_type }, _) => {
          return (
            <div className={`${stylesTable.comparisonContainer}`}>
              <span>{parcePercentage(roas)}</span>
              {isShowComparison && row_type !== 'total' && (
                <ComparisonChangeValue value={previous_period_roas_change} />
              )}
            </div>
          );
        },
      },
      {
        title: 'All Conversions',
        dataIndex: 'all_conversions',
        sorter: true,
        className: `${stylesTable.td}`,
        render: ({ all_conversions }, _) => {
          return (+all_conversions)?.toFixed(0);
        },
      },
    ];

    if (metrics.length) {
      const filteredColumns = columns.filter(column =>
        metrics.includes(column.dataIndex),
      );

      return filteredColumns;
    }

    return columns;
  }, [metrics]);

  return (
    <Table
      columns={getColumns}
      dataSource={data}
      isLoading={isLoading}
      unSortLastElement
      rowClassNameProvider={record => {
        const baseClassName = stylesTable.tr;
        const additionalClassName =
          record?.row_type === 'total' ? stylesTable.trTotal : '';

        return `${baseClassName} ${additionalClassName}`;
      }}
    />
  );
};

export default CampaignPerformanceTable;
